/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';

// also listen on a same-origin parent frame (e.g. Storybook), which may hold focus
const getListenerWindows = (): Window[] => {
  const windows = [window];
  try {
    if (window.parent !== window && window.parent.document) {
      windows.push(window.parent as Window & typeof globalThis);
    }
  } catch {
    // ignore: cross-origin parent
  }
  return windows;
};

export const useModifierKeyHeld = (isEnabled = true): boolean => {
  const [isHeld, setIsHeld] = React.useState(false);

  React.useEffect(() => {
    if (!isEnabled) {
      setIsHeld(false);
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey) {
        setIsHeld(true);
      }
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === 'Meta' || event.key === 'Control') {
        setIsHeld(false);
      }
    };
    const reset = (): void => setIsHeld(false);
    const listenerWindows = getListenerWindows();
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        reset();
      }
    };
    listenerWindows.forEach((listenerWindow) => {
      listenerWindow.addEventListener('keydown', onKeyDown);
      listenerWindow.addEventListener('keyup', onKeyUp);
      listenerWindow.addEventListener('blur', reset);
    });
    document.addEventListener('visibilitychange', onVisibilityChange);
    return (): void => {
      listenerWindows.forEach((listenerWindow) => {
        listenerWindow.removeEventListener('keydown', onKeyDown);
        listenerWindow.removeEventListener('keyup', onKeyUp);
        listenerWindow.removeEventListener('blur', reset);
      });
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isEnabled]);

  return isHeld;
};
